import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  Sheet,
  SheetContent,
  SheetPortal,
  SheetOverlay,
} from '@/components/ui/Sheet';
import { useVisibleViewport } from '@/lib/useVisibleViewport';
import { cn } from '@/lib/utils';

/**
 * BottomSheet — Asana / Linear / Notion–style modal sheet anchored to
 * the bottom edge of the viewport, with a drag handle, rounded top
 * corners, and an iOS-safe-area inset on the trailing edge.
 *
 * This is the canonical mobile surface for:
 *   - faceted filter pickers (replaces the side-anchored Sheet that
 *     gets clipped to a narrow strip on a 375 px phone)
 *   - sort menu
 *   - column visibility toggles
 *   - inline cell editing (tap a `Status` chip on a card → BottomSheet
 *     opens with the Status picker, large hit targets, keyboard-aware)
 *   - action menus (delete / archive / mark-as-paid)
 *
 * Composition:
 *   <BottomSheet open={…} onOpenChange={…}>
 *     <BottomSheetHeader title="Filter transactions" description="…" />
 *     <BottomSheetBody>… form / list …</BottomSheetBody>
 *     <BottomSheetFooter>
 *       <Button variant="ghost">Reset</Button>
 *       <Button>Apply</Button>
 *     </BottomSheetFooter>
 *   </BottomSheet>
 *
 * Height behavior is controlled by the body — `BottomSheetBody` is
 * `overflow-y-auto` and the outer content caps at 92% of the visible
 * viewport, so tall content scrolls inside the sheet while short
 * content snaps to its natural height (Asana's "menu-style" sheet).
 *
 * While open, the sheet tracks the visual viewport: when the on-screen
 * keyboard comes up it lifts to sit on top of the keyboard and shrinks
 * to the space above it, so a focused input stays in view instead of
 * the browser panning the sheet's top edge off screen.
 */

interface BottomSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  /** Custom class on the sheet content (rare; the default styling is
   *  already tuned to the brand palette). */
  className?: string;
}

export function BottomSheet({
  open,
  onOpenChange,
  children,
  className,
}: BottomSheetProps) {
  const viewport = useVisibleViewport(open);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="bottom"
        hideCloseButton
        // Tell Radix to skip its default focus-on-open behavior so
        // the keyboard doesn't immediately pop on tap-to-filter
        // (the operator hasn't asked for an input yet). Inputs inside
        // `BottomSheetBody` can still autoFocus explicitly when that
        // *is* the desired UX (e.g. an inline edit sheet).
        onOpenAutoFocus={(e) => e.preventDefault()}
        style={
          viewport
            ? {
                bottom: viewport.bottomInset,
                maxHeight: Math.round(viewport.height * 0.92),
              }
            : undefined
        }
        className={cn(
          'pb-[env(safe-area-inset-bottom)]',
          className,
        )}
      >
        {/* Drag handle. Decorative — the sheet still dismisses on
            overlay tap / Escape / Back button. We don't wire up a real
            swipe-to-dismiss gesture because Radix Dialog's overlay
            click already covers that intent on touch. */}
        <div className="flex shrink-0 justify-center pt-2.5 pb-1">
          <div
            aria-hidden
            className="h-1 w-10 rounded-full bg-border"
          />
        </div>
        {children}
      </SheetContent>
    </Sheet>
  );
}

/**
 * `BottomSheetHeader` — title + optional description above the body.
 * Stays sticky to the top of the sheet so the title remains visible
 * while the body scrolls (Asana / Notion pattern).
 */
export function BottomSheetHeader({
  title,
  description,
  className,
}: {
  title: React.ReactNode;
  description?: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'sticky top-0 z-10 flex shrink-0 flex-col gap-0.5 border-b border-border bg-card px-5 pb-3 pt-1',
        className,
      )}
    >
      <DialogPrimitive.Title className="text-base font-medium tracking-headline leading-headline text-foreground">
        {title}
      </DialogPrimitive.Title>
      {description && (
        <DialogPrimitive.Description className="text-xs text-muted-foreground">
          {description}
        </DialogPrimitive.Description>
      )}
    </div>
  );
}

/**
 * `BottomSheetBody` — scrollable middle region. Always set as
 * `overflow-y-auto` with overscroll containment so a flick-scroll
 * inside the sheet doesn't bubble up and scroll the underlying page.
 */
export function BottomSheetBody({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex-1 overflow-y-auto overscroll-contain px-5 py-4',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * `BottomSheetFooter` — pinned action row. Buttons stretch to the
 * full sheet width on the smallest screens and collapse to a normal
 * trailing-aligned row on tablets.
 */
export function BottomSheetFooter({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex shrink-0 flex-col-reverse gap-2 border-t border-border bg-card px-5 py-3 sm:flex-row sm:justify-end',
        className,
      )}
    >
      {children}
    </div>
  );
}

// Re-export the underlying portal/overlay so consumers that need to
// stack a modal *over* a BottomSheet can portal correctly.
export { SheetPortal as BottomSheetPortal, SheetOverlay as BottomSheetOverlay };
